<template>
  <div class="complete-wrapper">
    <b-loading :is-full-page="true" :active="loading"></b-loading>

    <div class="box complete-card is-light">
      <!-- Logo -->
      <div class="image-wrap">
        <img src="/images/TC wired for Gant Travel.png" alt="TripCircuit" />
      </div>

      <!-- Success icon -->
      <div class="icon-wrap">
        <span class="check-circle">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M5 11l5 5L17 7" stroke="#3B6D11" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </span>
      </div>

      <h2 class="complete-title">Profile created</h2>
      <p class="complete-subtitle">
        Your traveler profile has been created.
      </p>

      <p class="complete-subtitle">
        Set up your <strong>Gant ID</strong> for online access to book and manage
        your own travel.
      </p>

      <!-- Auth0 error -->
      <b-message v-if="auth0Error" type="is-danger" class="mb-4">
        {{ auth0Error }}
      </b-message>

      <!-- Benefits -->
      <div class="benefits-box">
        <p class="benefits-heading">Your Gant ID gives you:</p>
        <ul class="benefits-list">
          <li>- One secure sign in across TripCircuit, Gant Gateway and Gant360</li>
          <li>- Online booking and management of your trips, 24/7</li>
          <li>- Control over your travel preferences</li>
        </ul>
      </div>

      <!-- CTA -->
      <b-button
        type="is-info"
        class="is-gant mt-3"
        expanded
        :loading="loading"
        data-test="gant_id_signup_button"
        @click="setupLogin"
      >
        Create your Gant ID
      </b-button>

      <b-button
        type="is-light"
        class="mt-3"
        expanded
        :loading="loading"
        data-test="gant_id_email_code_button"
        @click="emailMeSignInCode"
      >
        Email me a one-time code
      </b-button>

      <div class="skip-row">
        Already have a Gant ID?
        <a
          class="skip-link"
          data-test="gant_id_login_link"
          @click="signInWithGantId"
          >Sign in</a
        >
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegisterComplete',

  data() {
    return {
      email:          '',
      registrationId: null,
      loading:        false,
      auth0Error:     '',
    };
  },

  created() {
    this.email          = sessionStorage.getItem('reg_email') || '';
    this.registrationId = sessionStorage.getItem('reg_registrationId') || null;

    // Handle Auth0 PKCE callback — ?code= means Auth0 redirected back here
    if (this.$route.query.code) {
      this.handleAuth0Callback();
    }
  },

  methods: {
    // ---- Redirect to Auth0 Universal Login (signup/login) ----

    setupLogin() {
      this.startAuth0Authorize({ screenHint: 'signup' });
    },

    emailMeSignInCode() {
      this.startAuth0Authorize({
        prompt: 'login',
        connection: this.$appConfig.auth0PasswordlessConnection || 'email',
      });
    },

    signInWithGantId() {
      this.startAuth0Authorize({});
    },

    startAuth0Authorize({ screenHint, prompt, connection }) {
      const domain      = this.$appConfig.auth0Domain;
      const clientId    = this.$appConfig.auth0ClientId;
      const audience    = this.$appConfig.auth0Audience;
      const callbackUrl = this.$appConfig.auth0CallbackUrl;

      if (!domain || !clientId || !callbackUrl) {
        this.auth0Error = 'Auth0 is not configured. Please contact support.';
        return;
      }

      // Build PKCE code_verifier / code_challenge
      const codeVerifier = this.generateCodeVerifier();
      sessionStorage.setItem('auth0_code_verifier', codeVerifier);

      this.generateCodeChallenge(codeVerifier).then((codeChallenge) => {
        const state = Math.random().toString(36).substring(2);
        sessionStorage.setItem('auth0_state', state);

        const params = new URLSearchParams({
          response_type:         'code',
          client_id:             clientId,
          redirect_uri:          callbackUrl,
          scope:                 'openid profile email',
          ...(audience ? { audience } : {}),
          login_hint:            this.email,
          ...(screenHint ? { screen_hint: screenHint } : {}),
          ...(prompt ? { prompt } : {}),
          ...(connection ? { connection } : {}),
          code_challenge:        codeChallenge,
          code_challenge_method: 'S256',
          state,
        });

        window.location.href = `https://${domain}/authorize?${params.toString()}`;
      });
    },

    // ---- Handle Auth0 callback — exchange code for tokens, auto-login ----

    async handleAuth0Callback() {
      this.loading = true;

      const code         = this.$route.query.code;
      const codeVerifier = sessionStorage.getItem('auth0_code_verifier');
      const domain       = this.$appConfig.auth0Domain;
      const clientId     = this.$appConfig.auth0ClientId;
      const callbackUrl  = this.$appConfig.auth0CallbackUrl;
      const sfContactId  = sessionStorage.getItem('reg_sfContactId');

      if (!code || !codeVerifier) {
        this.loading    = false;
        this.auth0Error = 'Auth0 callback is invalid. Please try again.';
        return;
      }

      try {
        // Exchange code for tokens
        const tokenResponse = await fetch(`https://${domain}/oauth/token`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grant_type:    'authorization_code',
            client_id:     clientId,
            code,
            code_verifier: codeVerifier,
            redirect_uri:  callbackUrl,
          }),
        });

        const tokens = await tokenResponse.json();

        if (!tokenResponse.ok || !tokens.id_token) {
          throw new Error(tokens.error_description || 'Token exchange failed.');
        }

        // Establish local session — satisfies router.beforeEach auth guard
        const expireTime = new Date().getTime() + this.$appConfig.routerTimer;
        localStorage.setItem('loginExpireTime', expireTime);

        // Load the user's full travel profile and hydrate the Vuex store
        if (sfContactId) {
          const profileResult = await this.$app.huddleLabsServerCall('profile/travel-profile', {
            contactId:      sfContactId,
            includeRelated: [
              'travelerPrograms',
              'paymentMethods',
              'unusedTickets',
              'travelPolicy',
              'preferredVendors',
              'tripcircuitConfiguration',
              'accountNotes',
            ],
          });

          if (profileResult && profileResult.data) {
            this.$store.dispatch('travellersAndTrips/addProfile', profileResult.data);
            this.$store.commit('travellersAndTrips/SET_TRAVELLER_PROFILE', profileResult.data);

            if (profileResult.data.accountNotes) {
              this.$store.dispatch('travellersAndTrips/setAccountNotes', profileResult.data.accountNotes);
            }
          }
        }

        // Initialise travellers state (mirrors Login.vue ok())
        this.$store.commit('travellersAndTrips/EDIT_TRAVELLERS', {
          tripType: 'business',
          bookFor:  'user',
          others:   [],
          set:      true,
          business: true,
          self:     true,
        });

        // Load shared resources (vendors, options, airports, etc.) — mirrors Login.vue getCodeConverter()
        this.getCodeConverter();

        // Clean up registration session data
        sessionStorage.removeItem('auth0_code_verifier');
        sessionStorage.removeItem('auth0_state');
        sessionStorage.removeItem('reg_registrationId');
        sessionStorage.removeItem('reg_email');
        sessionStorage.removeItem('reg_sfContactId');

        // Auto-login — take the user straight to the home page
        this.$router.push('/home');
      } catch (err) {
        this.loading    = false;
        this.auth0Error = err.message || 'Auth0 setup failed. Please try again.';
      }
    },

    // ---- Load shared resources (vendors, options, airports, etc.) ----

    getCodeConverter() {
      this.$app.huddleLabsServerCall('resources/vendors').then((result) => {
        this.$store.dispatch('bookTravel/vendors', result);
      });

      this.$app.huddleLabsServerCall('resources/options').then((result) => {
        this.$store.dispatch('options/setOptions', result.data);
      });

      this.$app
        .huddleLabsServerCall('resources/airports?mode=advanced')
        .then((result) => {
          this.$store.dispatch('bookTravel/airports', result.data);
        });

      this.$app
        .huddleLabsServerCall('resources/airline-unaccompanied-minors')
        .then((result) => {
          this.$store.dispatch(
            'travellersAndTrips/unaccompaniedMinorAirlineDetails',
            result.data
          );
        });

      this.$app
        .huddleLabsServerCall('profile/travel-policy-attributes', null)
        .then((result) => {
          this.$store.dispatch(
            'travelPolicy/addTravelPolicyAttributes',
            result.data
          );
        });
    },

    // ---- PKCE helpers ----

    generateCodeVerifier() {
      const array = new Uint8Array(32);
      window.crypto.getRandomValues(array);
      return Array.from(array, (b) => ('0' + b.toString(16)).slice(-2)).join('');
    },

    async generateCodeChallenge(verifier) {
      const encoder = new TextEncoder();
      const data    = encoder.encode(verifier);
      const digest  = await window.crypto.subtle.digest('SHA-256', data);
      return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
    },

    decodeJwtSub(token) {
      try {
        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return decoded.sub || null;
      } catch (_) {
        return null;
      }
    },
  },
};
</script>

<style scoped>
.complete-wrapper {
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
}

.complete-card {
  width: 500px;
  max-width: 100%;
  padding: 2rem;
  text-align: center;
  border-radius: 5px;
  align-self: flex-start;
}

.image-wrap {
  margin: 0 auto 1.25rem;
  width: 230px;
}
.image-wrap img { width: 100%; }

.icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.check-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #EAF3DE;
  border: 2px solid #639922;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.complete-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.complete-subtitle {
  font-size: 0.85rem;
  color: #666660;
  margin-bottom: 1.25rem;
  line-height: 1.6;
}

.benefits-box {
  background: #f4f4f0;
  border-radius: 8px;
  padding: 12px 16px;
  text-align: left;
  margin-bottom: 0.5rem;
}

.benefits-heading {
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 6px;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.82rem;
  color: #666660;
  line-height: 1.8;
}

.skip-row {
  margin-top: 0.85rem;
  font-size: 0.78rem;
  color: #666660;
}

.skip-link {
  color: #185FA5;
  cursor: pointer;
  margin-left: 4px;
  text-decoration: underline;
}
</style>
